import {
  ActiveTimePeriodEnum,
  CurrencyEnum,
  DateTypeEnum,
  SimpleAnswerEnum,
} from '../../enums/entity.enum';
import {
  CloseDateTypeEnum,
  ContractualDateTypeEnum,
  LiquidityTierEnum,
  MinGPCommitmentEnum,
  RankEnum,
  ReportingFrequencyEnum,
  ValuationMethodologyEnum,
} from '../../enums/closedEnd.enum';
import {
  ContractualFinalCloseExtension,
  ContractualInvestmentPeriodExtension,
  ContractualTermExtension,
  RecyclingTimeLimit,
  RecyclingProceedsLimit,
  LpClawbackPerLimit,
  LpClawbackTimeLimit,
  ClosedEndInterimDate,
} from '../entities/entities.model';
import { ClosedEndGPInformation } from './closed-end-gp-information';
import { ClosedEndGPTargetInvestmentStat } from './closed-end-gp-target-investment-stat';
import { ClosedEndKeyTerm } from './closed-end-key-term';
import { ClosedEndStructure } from './closed-end-structure';
import { ClosedEndTargetReturnProfile } from './closed-end-target-return-profile';
import { AccountingMethodEnum } from '../../enums/coInvestment.enum';
import { IClosedEndDetails } from '../../interfaces/investment-vehicle/closed-end-details.interface';
import { StoreModelDate, StoreModelType } from '@aksia/infrastructure';

export class ClosedEndDetails implements IClosedEndDetails {
  id: number | undefined = undefined;
  fundId: number | undefined = undefined;

  //#region <<< Section - Details Date Info >>>

  investmentPeriodExpirationDateType: CloseDateTypeEnum | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  investmentPeriodExpirationDate: Date | undefined = undefined;

  termExpirationDateType: CloseDateTypeEnum | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  termExpirationDate: Date | undefined = undefined;

  //#endregion

  //#region <<< Section - Fund Timing >>>

  contractualFinalCloseDateType: ContractualDateTypeEnum | undefined =
    undefined;
  contractualFinalCloseDateDesc: string | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  contractualFinalCloseDate: Date | undefined = undefined;
  contractualFinalCloseMonths: number | undefined = undefined;
  contractualFinalCloseUnlimitedExtensions: boolean | undefined = undefined;

  @StoreModelType(() => ContractualFinalCloseExtension)
  contractualFinalCloseExtensions:
    Array<ContractualFinalCloseExtension> | undefined = undefined;

  @StoreModelType(() => ContractualInvestmentPeriodExtension)
  contractualInvestmentPeriodExtensions:
    Array<ContractualInvestmentPeriodExtension> | undefined = undefined;

  @StoreModelType(() => ContractualTermExtension)
  contractualTermExtensions: Array<ContractualTermExtension> | undefined =
    undefined;

  //#endregion

  //#region <<< Section - Capital Call Procedure >>>

  followOnInvestmentsPerLimit: SimpleAnswerEnum | undefined = undefined;
  followOnInvestmentsPerLimitRate: number | undefined = undefined;
  followOnInvestmentsTimeLimit: SimpleAnswerEnum | undefined = undefined;
  followOnInvestmentsTimeLimitMonths: number | undefined = undefined;
  investmentsInProgressTimeLimit: SimpleAnswerEnum | undefined = undefined;
  investmentsInProgressTimeLimitMonths: number | undefined = undefined;

  //#endregion

  //#region <<< Section - Organizational Expense Cap >>>

  organizationalExpenseCapType: MinGPCommitmentEnum | undefined = undefined;
  organizationalExpenseCapRate: number | undefined = undefined;
  organizationalExpenseCapAmount: number | undefined = undefined;

  //#endregion

  //#region <<< Section - Recycling >>>

  @StoreModelType(() => RecyclingTimeLimit)
  recyclingTimeLimits: Array<RecyclingTimeLimit> | undefined = undefined;

  @StoreModelType(() => RecyclingProceedsLimit)
  recyclingProceedsLimits: Array<RecyclingProceedsLimit> | undefined =
    undefined;

  //#endregion

  //#region <<< Section - LP Clawback >>>

  lpClawbackPerLimit: SimpleAnswerEnum | undefined = undefined;

  @StoreModelType(() => LpClawbackPerLimit)
  lpClawbackPerLimits: Array<LpClawbackPerLimit> | undefined = undefined;
  lpClawbackTimeLimit: SimpleAnswerEnum | undefined = undefined;

  @StoreModelType(() => LpClawbackTimeLimit)
  lpClawbackTimeLimits: Array<LpClawbackTimeLimit> | undefined = undefined;

  //#endregion

  //#region <<< Section - Removal/Termination Rights >>>

  forFaultGpImRemoval: SimpleAnswerEnum | undefined = undefined;
  forFaultGpImVoteThresholdRate: number | undefined = undefined;
  carriedInterestReduction: SimpleAnswerEnum | undefined = undefined;
  carriedInterestReductionRate: number | undefined = undefined;
  forFaultInvPeriodTermination: SimpleAnswerEnum | undefined = undefined;
  forFaultInvPeriodVoteThresholdRate: number | undefined = undefined;
  forFaultFundTermination: SimpleAnswerEnum | undefined = undefined;
  forFaultFundVoteThresholdRate: number | undefined = undefined;
  noFaultGpImRemoval: SimpleAnswerEnum | undefined = undefined;
  noFaultGpImVoteThresholdRate: number | undefined = undefined;
  noFaultGpImTimeLimit: SimpleAnswerEnum | undefined = undefined;
  noFaultGpImPenalty: SimpleAnswerEnum | undefined = undefined;
  noFaultInvPeriodTermination: SimpleAnswerEnum | undefined = undefined;
  noFaultInvPeriodVoteThresholdRate: number | undefined = undefined;
  noFaultInvPeriodTimeLimit: SimpleAnswerEnum | undefined = undefined;
  noFaultInvPeriodPenalty: SimpleAnswerEnum | undefined = undefined;
  noFaultFundTermination: SimpleAnswerEnum | undefined = undefined;
  noFaultFundVoteThresholdRate: number | undefined = undefined;
  noFaultFundTimeLimit: SimpleAnswerEnum | undefined = undefined;
  noFaultFundPenalty: SimpleAnswerEnum | undefined = undefined;
  keyPersonProvision: SimpleAnswerEnum | undefined = undefined;
  automaticSuspensionLift: SimpleAnswerEnum | undefined = undefined;
  keyPersonProVoteThresholdRate: number | undefined = undefined;

  //#endregion

  //#region <<< Section - Leverage >>>

  bridgeFinancingTerm: number | undefined = undefined;
  subscriptionLines: SimpleAnswerEnum | undefined = undefined;

  //#endregion

  //#region <<< Section - Reporting >>>

  accountingMethod: AccountingMethodEnum | undefined = undefined;
  accountingMethodDescription: string | undefined = undefined;
  quarterlyReportsDeadline: number | undefined = undefined;
  annualReportsDeadline: number | undefined = undefined;
  reportingDateType: DateTypeEnum | undefined = undefined;
  fiscalYearEndDate: Date | undefined = undefined;
  pcapcasReportingFrequency: ReportingFrequencyEnum | undefined = undefined;
  reportingFrequencyDescription: string | undefined = undefined;
  valuationMethodology: ValuationMethodologyEnum | undefined = undefined;
  valuationMethodologyDescription: string | undefined = undefined;
  divergence: SimpleAnswerEnum | undefined = undefined;
  divergenceDescription: string | undefined = undefined;

  //#endregion

  //#region <<< Section - Successor Fund Provision >>>

  successorFundProvision: SimpleAnswerEnum | undefined = undefined;
  successorFundProvisionThresholdRate: number | undefined = undefined;

  //#endregion

  //#region <<< Section - Fund Terms >>>

  fundCommitementsCurrency: CurrencyEnum | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  fundCommitementsAsOf: Date | undefined = undefined;

  @StoreModelType(() => ClosedEndInterimDate)
  interimDates: Array<ClosedEndInterimDate> | undefined = undefined;
  liquidityTier: LiquidityTierEnum | undefined = undefined;

  //#endregion

  //#region <<< Section - Return Profile >>>

  medianQuartileRanking: RankEnum | undefined = undefined;
  tvpiNotes: string | undefined = undefined;

  //#endregion

  closedEndKeyTerm: ClosedEndKeyTerm | undefined = new ClosedEndKeyTerm();
  closedEndStructure: ClosedEndStructure | undefined = new ClosedEndStructure();
  closedEndGPInformation: ClosedEndGPInformation | undefined =
    new ClosedEndGPInformation();
  closedEndGPTargetInvestmentStat: ClosedEndGPTargetInvestmentStat | undefined =
    new ClosedEndGPTargetInvestmentStat();
  closedEndTargetReturnProfile: ClosedEndTargetReturnProfile | undefined =
    new ClosedEndTargetReturnProfile();
}
